import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRecentActivity } from '../lib/statsService';
import type { ActivityEvent } from '../types';
import { logger } from '../lib/logger';

const eventTypeIcons: Record<string, string> = {
  inscription: '🆕',
  candidature: '📨',
  abonnement: '💳',
  match: '🤝',
  signalement: '🚩',
  video: '🎬',
  offre: '📋',
};

const eventTypeColors: Record<string, string> = {
  inscription: 'bg-blue-100 text-blue-600',
  candidature: 'bg-blue-100 text-blue-600',
  abonnement: 'bg-purple-100 text-purple-600',
  match: 'bg-teal-100 text-teal-600',
  signalement: 'bg-red-100 text-red-600',
  video: 'bg-orange-100 text-orange-600',
  offre: 'bg-amber-100 text-amber-600',
};

const eventTypeLabels: Record<string, string> = {
  inscription: 'Inscription',
  candidature: 'Candidature',
  abonnement: 'Abonnement',
  match: 'Match',
  signalement: 'Signalement',
  video: 'Vidéo',
  offre: 'Offre',
};

const Activity: React.FC = () => {
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  const load = async () => {
    setLoading(true);
    try {
      const events = await fetchRecentActivity();
      setActivity(events);
    } catch (err) {
      logger.error('load_activity', 'Activity', { error: String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const types = Array.from(new Set(activity.map((e) => e.type)));
  const filtered = filter === 'all' ? activity : activity.filter((e) => e.type === filter);

  return (
    <div className="space-y-5">
      {/* Filtres par type */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filter === 'all' ? 'bg-white text-[#00244F] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tout
          </button>
          {types.map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                filter === type ? 'bg-white text-[#00244F] shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{eventTypeIcons[type] || '📌'}</span>
              {eventTypeLabels[type] || type}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
        >
          ↻ Rafraîchir
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Activité récente</h3>
          <span className="text-xs text-gray-400">{filtered.length} évènement(s)</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-gray-400">
                Aucune activité récente
              </div>
            ) : (
              filtered.map((event) => {
                const clickable = !!event.link;
                return (
                  <div
                    key={event.id}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? () => navigate(event.link!) : undefined}
                    onKeyDown={clickable ? (e) => { if (e.key === 'Enter') navigate(event.link!); } : undefined}
                    className={`px-5 py-3.5 flex items-center gap-4 transition-colors ${
                      clickable ? 'cursor-pointer hover:bg-gray-50' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg ${eventTypeColors[event.type] || 'bg-gray-100 text-gray-600'} flex items-center justify-center text-sm flex-shrink-0`}>
                      {eventTypeIcons[event.type] || '📌'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{event.description}</p>
                      <p className="text-xs text-gray-400">{event.user}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-500">{event.date}</p>
                      <p className="text-xs text-gray-400">{event.time}</p>
                    </div>
                    {clickable && (
                      <span className="text-gray-300 flex-shrink-0">›</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;
